import { st, classes } from './Content.st.css';
import React, { useState, useCallback } from 'react';
import Divider from '../../../Divider';
import ScrollableContainer, {
  AreaY,
  ScrollAreaData,
} from '../../../common/ScrollableContainer';
import { dataHooks } from '../../constants';
import { useBaseModalLayoutContext } from '../../BaseModalLayoutContext';
import { ContentProps } from './Content.types';

export const Content = ({
  dataHook,
  className,
  children,
  hideTopScrollDivider = false,
  hideBottomScrollDivider = false,
  overflowY = 'auto',
  scrollProps = {},
}: ContentProps) => {
  const { contentClassName, content = children } = useBaseModalLayoutContext();
  const [scrollAreaY, setScrollAreaY] = useState(AreaY.NONE);
  const { onScrollAreaChanged } = scrollProps;
  const hideContentDividers = hideTopScrollDivider && hideBottomScrollDivider;

  const handleScrollAreaChanged = useCallback(
    ({ area, target }: ScrollAreaData) => {
      if (scrollAreaY !== area.y) {
        if (!hideContentDividers) {
          setScrollAreaY(area.y);
        }
        if (onScrollAreaChanged) {
          onScrollAreaChanged({ area, target });
        }
      }
    },
    [hideContentDividers, onScrollAreaChanged, scrollAreaY],
  );

  const isTopDividerHidden = useCallback(
    () =>
      hideTopScrollDivider ||
      scrollAreaY === AreaY.TOP ||
      scrollAreaY === AreaY.NONE,
    [hideTopScrollDivider, scrollAreaY],
  );

  const isBottomDividerHidden = useCallback(
    () =>
      hideBottomScrollDivider ||
      scrollAreaY === AreaY.BOTTOM ||
      scrollAreaY === AreaY.NONE,
    [hideBottomScrollDivider, scrollAreaY],
  );

  const registerToScrollAreaChanges =
    !hideContentDividers || !!onScrollAreaChanged;

  return (
    (content && (
      <div
        data-hook={dataHook}
        data-hidedividers={hideContentDividers}
        style={{ overflowY }}
        className={st(
          classes.root,
          {
            hideTopDivider: isTopDividerHidden(),
            hideBottomDivider: isBottomDividerHidden(),
          },
          contentClassName,
          className,
        )}
      >
        {!hideTopScrollDivider && <Divider className={classes.topDivider} />}
        <ScrollableContainer
          style={{ overflowY }}
          dataHook={dataHooks.contentWrapper}
          className={classes.innerContent}
          onScrollAreaChanged={
            (registerToScrollAreaChanges && handleScrollAreaChanged) ||
            undefined
          }
        >
          {content}
        </ScrollableContainer>
        {!hideBottomScrollDivider && (
          <Divider className={classes.bottomDivider} />
        )}
      </div>
    )) ||
    null
  );
};

Content.displayName = 'BaseModalLayout.Content';
